import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBudget } from "../context/BudgetContext";
import { useBilling } from "../context/BillingContext";

const YOUTUBE_CSV_URL = "https://www.youtube.com/results?search_query=how+to+download+bank+statement+csv";
const FREE_LIMIT = 50;

export default function Transactions() {
  const {
    transactions, categories, addTransaction, deleteTransaction,
    recurringTransactions, addRecurring, deleteRecurring, toggleRecurring,
    budgetLoading,
  } = useBudget();
  const { isPro } = useBilling();
  const navigate = useNavigate();

  const [mode, setMode] = useState("none");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({ description: "", amount: "", date: "", category: "" });
  const [fileName, setFileName] = useState("");
  const [csvError, setCsvError] = useState("");
  const [csvSuccess, setCsvSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  // Recurring form
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [rDesc, setRDesc] = useState("");
  const [rAmount, setRAmount] = useState("");
  const [rCatId, setRCatId] = useState("");
  const [rDay, setRDay] = useState("1");

  const fileRef = useRef();

  if (budgetLoading) return <div className="page-loading" />;

  const atLimit = !isPro && transactions.length >= FREE_LIMIT;
  const toggleMode = (next) => setMode(prev => prev === next ? "none" : next);

  const handleAdd = () => {
    if (atLimit || !desc || !amount || isNaN(Number(amount))) return;
    addTransaction({ description: desc, amount: Number(amount), categoryId: categoryId ? Number(categoryId) : null, date });
    setDesc(""); setAmount(""); setCategoryId(""); setDate(new Date().toISOString().split("T")[0]); setMode("none");
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return { headers: [], rows: [] };
    const hdrs = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    const rows = lines.slice(1).map(line => line.split(",").map(v => v.trim().replace(/^"|"$/g, ""))).map(vals => Object.fromEntries(hdrs.map((h, i) => [h, vals[i] ?? ""])));
    return { headers: hdrs, rows };
  };

  const handleFile = (e) => {
    setCsvError(""); setCsvSuccess(false);
    const file = e.target.files[0]; if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: hdrs } = parseCSV(ev.target.result);
      if (!hdrs.length) { setCsvError("Could not parse CSV."); return; }
      setHeaders(hdrs);
      const lower = hdrs.map(h => h.toLowerCase());
      setMapping({
        description: hdrs[lower.findIndex(h => h.includes("desc") || h.includes("name") || h.includes("memo"))] ?? "",
        amount: hdrs[lower.findIndex(h => h.includes("amount") || h.includes("sum"))] ?? "",
        date: hdrs[lower.findIndex(h => h.includes("date"))] ?? "",
        category: hdrs[lower.findIndex(h => h.includes("cat") || h.includes("type"))] ?? "",
      });
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!mapping.description || !mapping.amount) { setCsvError("Map description and amount first."); return; }
    const file = fileRef.current.files[0];
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { rows } = parseCSV(ev.target.result);
      let count = transactions.length;
      rows.forEach(row => {
        if (!isPro && count >= FREE_LIMIT) return;
        const amt = parseFloat(row[mapping.amount]?.replace(/[^0-9.-]/g, "") || "0");
        if (isNaN(amt) || amt <= 0) return;
        const catName = mapping.category ? row[mapping.category] : "";
        const matchedCat = categories.find(c => c.name.toLowerCase() === catName?.toLowerCase());
        addTransaction({ description: row[mapping.description] || "Imported", amount: amt, date: mapping.date ? row[mapping.date] : new Date().toISOString().split("T")[0], categoryId: matchedCat?.id ?? null });
        count++;
      });
      setCsvSuccess(true); setHeaders([]); setFileName("");
      setTimeout(() => { setCsvSuccess(false); setMode("none"); }, 2000);
    };
    reader.readAsText(file);
  };

  // Guard against CSV/formula injection: a leading =, +, -, @ (or control char)
  // can be interpreted as a formula by Excel/Sheets. Neutralize with a leading
  // apostrophe, and escape embedded quotes.
  const csvCell = (value) => {
    let s = String(value ?? "");
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleExportCSV = () => {
    const rows = [
      ["Date", "Description", "Category", "Amount"],
      ...[...transactions].reverse().map(tx => [
        tx.date, tx.description,
        categories.find(c => c.id === tx.categoryId)?.name ?? "",
        tx.amount,
      ]),
    ];
    const csv = rows.map(r => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleAddRecurring = () => {
    if (!rDesc || !rAmount || isNaN(Number(rAmount))) return;
    addRecurring({ description: rDesc, amount: Number(rAmount), categoryId: rCatId ? Number(rCatId) : null, dayOfMonth: Number(rDay) });
    setRDesc(""); setRAmount(""); setRCatId(""); setRDay("1");
  };

  const filtered = [...transactions].reverse().filter(tx => {
    const matchSearch = !search || tx.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || String(tx.categoryId) === filterCat;
    const matchMonth = !filterMonth || (tx.date && tx.date.startsWith(filterMonth));
    return matchSearch && matchCat && matchMonth;
  });

  const hasFilter = search || filterCat || filterMonth;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Transactions</div>
          <div className="page-subtitle">{transactions.length} total</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className={`btn${mode === "manual" ? " btn-green" : ""}`} onClick={() => toggleMode("manual")}>+ add</button>
          <button className={`btn${mode === "csv" ? " btn-green" : ""}`} onClick={() => toggleMode("csv")}>↑ csv</button>
          {transactions.length > 0 && (
            <button className="btn" onClick={handleExportCSV}>↓ csv</button>
          )}
        </div>
      </div>

      {/* Free plan limit banner */}
      {atLimit && (
        <div className="card hover-amber" style={{ marginBottom: 10, borderColor: "#f59e0b22", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="mono" style={{ fontSize: 11, color: "#f59e0b" }}>
            free plan limit — {FREE_LIMIT} transactions max
          </span>
          <button className="btn btn-green" onClick={() => navigate("/app/billing")} style={{ fontSize: 10 }}>
            upgrade →
          </button>
        </div>
      )}

      {/* Manual form */}
      {mode === "manual" && (
        <div className="card hover-green" style={{ marginBottom: 10, borderColor: "#22c55e22" }}>
          <div className="label" style={{ marginBottom: 10 }}>NEW_TRANSACTION</div>
          {atLimit ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="mono" style={{ fontSize: 11, color: "#f59e0b" }}>upgrade to add more transactions</span>
              <button className="btn btn-green" onClick={() => navigate("/app/billing")} style={{ fontSize: 10 }}>upgrade →</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input style={{ flex: "2 1 140px" }} placeholder="description" value={desc} onChange={e => setDesc(e.target.value)} />
              <input style={{ flex: "1 1 80px" }} type="number" placeholder="amount" value={amount} onChange={e => setAmount(e.target.value)} />
              <select style={{ flex: "1 1 110px" }} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                <option value="">no category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input style={{ flex: "1 1 110px" }} type="date" value={date} onChange={e => setDate(e.target.value)} />
              <button className="btn btn-green" onClick={handleAdd}>save</button>
            </div>
          )}
        </div>
      )}

      {/* CSV import */}
      {mode === "csv" && (
        <div className="card hover-blue" style={{ marginBottom: 10, borderColor: "#3b82f622" }}>
          <div className="label" style={{ marginBottom: 12 }}>CSV_IMPORT</div>
          {!isPro ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="mono" style={{ fontSize: 11, color: "#555" }}>pro plan required to import from csv</span>
              <button className="btn btn-green" onClick={() => navigate("/app/billing")} style={{ fontSize: 10 }}>upgrade →</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span />
                <a href={YOUTUBE_CSV_URL} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 10, color: "#ef4444", fontFamily: "var(--mono)", textDecoration: "none" }}>
                  ▶ how to export bank csv
                </a>
              </div>
              <div
                onClick={() => fileRef.current.click()}
                onDrop={e => { e.preventDefault(); handleFile({ target: { files: [e.dataTransfer.files[0]] } }); }}
                onDragOver={e => e.preventDefault()}
                style={{ border: "1px dashed #1a1a1a", borderRadius: 6, padding: "16px", textAlign: "center", cursor: "pointer", marginBottom: 10 }}
              >
                <div className="mono" style={{ fontSize: 11, color: "#777" }}>{fileName || "drop csv or click to browse"}</div>
                <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleFile} />
              </div>
              {csvError && <div className="badge badge-red" style={{ marginBottom: 8, display: "block" }}>{csvError}</div>}
              {csvSuccess && <div className="badge badge-green" style={{ marginBottom: 8, display: "block" }}>✓ imported</div>}
              {headers.length > 0 && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                    {[["description", "Description *"], ["amount", "Amount *"], ["date", "Date"], ["category", "Category"]].map(([key, label]) => (
                      <div key={key}>
                        <div className="label" style={{ marginBottom: 3 }}>{label}</div>
                        <select value={mapping[key]} onChange={e => setMapping(m => ({ ...m, [key]: e.target.value }))}>
                          <option value="">— skip —</option>
                          {headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-green" onClick={handleImport}>import transactions</button>
                </>
              )}
              <div style={{ marginTop: 12, background: "#050505", borderRadius: 6, padding: "8px 10px" }}>
                <div className="label" style={{ marginBottom: 4 }}>expected format</div>
                <code className="mono" style={{ fontSize: 10, color: "#22c55e55", lineHeight: 1.8 }}>
                  date,description,amount,category<br />
                  2024-01-15,Groceries,85.40,Food
                </code>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filter bar */}
      {transactions.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
          <input
            placeholder="search transactions…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: "2 1 140px" }}
          />
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            style={{ flex: "1 1 120px" }}
          >
            <option value="">all categories</option>
            {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>
          <input
            type="month"
            value={filterMonth}
            onChange={e => setFilterMonth(e.target.value)}
            style={{ flex: "1 1 130px" }}
          />
          {hasFilter && (
            <button className="btn" onClick={() => { setSearch(""); setFilterCat(""); setFilterMonth(""); }} style={{ whiteSpace: "nowrap" }}>
              clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {transactions.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <div className="mono" style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>no transactions yet</div>
            <div className="mono" style={{ fontSize: 10, color: "#1a1a1a" }}>add one manually or import a csv above</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <span className="mono" style={{ fontSize: 11, color: "#555" }}>no results</span>
          </div>
        ) : (
          <table className="gl-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(tx => (
                <tr key={tx.id}>
                  <td className="mono" style={{ color: "#666", fontSize: 11 }}>{tx.date}</td>
                  <td style={{ color: "#888" }}>
                    {tx.description}
                    {tx.recurring && <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 9 }}>auto</span>}
                  </td>
                  <td><span className="badge badge-gray">{categories.find(c => c.id === tx.categoryId)?.name ?? "—"}</span></td>
                  <td className="mono" style={{ color: "#ef4444", fontSize: 12 }}>-${Number(tx.amount).toLocaleString()}</td>
                  <td>
                    {confirmId === tx.id ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-danger" onClick={() => { deleteTransaction(tx.id); setConfirmId(null); }} style={{ padding: "3px 8px", fontSize: 10 }}>sure?</button>
                        <button className="btn" onClick={() => setConfirmId(null)} style={{ padding: "3px 6px", fontSize: 10 }}>✕</button>
                      </div>
                    ) : (
                      <button className="btn btn-danger" onClick={() => setConfirmId(tx.id)} style={{ padding: "3px 8px", fontSize: 10 }}>del</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filtered.length > 0 && hasFilter && (
        <div className="mono" style={{ fontSize: 10, color: "#555", marginTop: 8, textAlign: "right" }}>
          {filtered.length} of {transactions.length} transactions
        </div>
      )}

      {/* Recurring transactions */}
      <div className="card" style={{ marginTop: 12 }}>
        <button
          onClick={() => setRecurringOpen(o => !o)}
          style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "100%", padding: 0 }}
        >
          <span className="label" style={{ marginBottom: 0 }}>RECURRING_TRANSACTIONS</span>
          <span className="badge badge-gray" style={{ marginLeft: 2 }}>{recurringTransactions.length}</span>
          <span className="mono" style={{ fontSize: 10, color: "#666", marginLeft: "auto" }}>{recurringOpen ? "▲" : "▼"}</span>
        </button>

        {recurringOpen && (
          <div style={{ marginTop: 12 }}>
            {/* Add recurring form */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              <input style={{ flex: "2 1 130px" }} placeholder="description" value={rDesc} onChange={e => setRDesc(e.target.value)} />
              <input style={{ flex: "1 1 80px" }} type="number" placeholder="amount" value={rAmount} onChange={e => setRAmount(e.target.value)} />
              <select style={{ flex: "1 1 110px" }} value={rCatId} onChange={e => setRCatId(e.target.value)}>
                <option value="">no category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input
                style={{ flex: "0 1 70px" }} type="number" min="1" max="28"
                placeholder="day" value={rDay} onChange={e => setRDay(e.target.value)}
                title="Day of month (1–28)"
              />
              <button className="btn btn-green" onClick={handleAddRecurring} style={{ fontSize: 10 }}>+ add</button>
            </div>

            {recurringTransactions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <span className="mono" style={{ fontSize: 10, color: "#555" }}>no recurring transactions</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {recurringTransactions.map(r => (
                  <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, background: "#050505", border: "1px solid #111", borderRadius: 6, padding: "8px 10px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span className="mono" style={{ fontSize: 11, color: r.active !== false ? "#666" : "#333" }}>{r.description}</span>
                        <span className="mono" style={{ fontSize: 10, color: "#ef4444" }}>-${Number(r.amount).toLocaleString()}</span>
                        <span className="badge badge-gray" style={{ fontSize: 9 }}>day {r.dayOfMonth ?? 1}</span>
                        {categories.find(c => c.id === r.categoryId) && (
                          <span className="badge badge-gray" style={{ fontSize: 9 }}>
                            {categories.find(c => c.id === r.categoryId).name}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className={`btn${r.active !== false ? " btn-green" : ""}`}
                      onClick={() => toggleRecurring(r.id)}
                      style={{ fontSize: 9, padding: "2px 7px" }}
                    >
                      {r.active !== false ? "on" : "off"}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => deleteRecurring(r.id)}
                      style={{ fontSize: 9, padding: "2px 7px" }}
                    >
                      del
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
